/** IDs de rol Simappe para `read-by-roles-grouped` (menú lateral / productos). */

function parseRoleId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function collectRoleIdsFromRoleArray(roles: unknown): number[] {
  if (!Array.isArray(roles)) return [];
  const ids: number[] = [];
  for (const role of roles) {
    if (role && typeof role === 'object') {
      const id = parseRoleId((role as { id?: unknown }).id);
      if (id !== null) ids.push(id);
      continue;
    }
    const scalar = parseRoleId(role);
    if (scalar !== null) ids.push(scalar);
  }
  return ids;
}

function collectRoleIdsFromClaim(value: unknown): number[] {
  if (Array.isArray(value)) return collectRoleIdsFromRoleArray(value);
  const single = parseRoleId(value);
  return single !== null ? [single] : [];
}

function decodeAccessTokenPayload(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Resuelve IDs de rol desde `user_data`, cookie de perfil y claims del JWT actual.
 * El menú lateral depende de estos IDs; si faltan, `read-by-roles-grouped` devuelve vacío.
 */
export function collectSimappeMenuRoleIds(): number[] {
  const ids = new Set<number>();

  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      const userData = JSON.parse(raw) as { roles?: unknown };
      collectRoleIdsFromRoleArray(userData.roles).forEach((id) => ids.add(id));
    }
  } catch {
    /* ignore */
  }

  const payload = decodeAccessTokenPayload();
  if (payload) {
    collectRoleIdsFromRoleArray(payload['roles']).forEach((id) => ids.add(id));
    collectRoleIdsFromClaim(payload['roleIds']).forEach((id) => ids.add(id));
    collectRoleIdsFromClaim(payload['roleId']).forEach((id) => ids.add(id));
    const user = payload['User'] ?? payload['user'];
    if (user && typeof user === 'object') {
      collectRoleIdsFromRoleArray((user as { roles?: unknown }).roles).forEach((id) => ids.add(id));
      collectRoleIdsFromClaim((user as { roleIds?: unknown }).roleIds).forEach((id) => ids.add(id));
      collectRoleIdsFromClaim((user as { roleId?: unknown }).roleId).forEach((id) => ids.add(id));
    }
  }

  return Array.from(ids);
}

/**
 * Asegura que `user_data.roles` tenga `id` numérico (el menú Simappe lo exige).
 * Se llama antes de `read-by-roles-grouped`.
 */
export function syncRoleIdsIntoUserData(): void {
  if (typeof localStorage === 'undefined') return;

  const ids = collectSimappeMenuRoleIds();
  if (!ids.length) return;

  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return;

    const userData = JSON.parse(raw) as {
      roles?: Array<{ id?: unknown; name?: string; authority?: string }>;
    };

    if (!Array.isArray(userData.roles)) {
      userData.roles = ids.map((id) => ({ id, name: '', authority: '' }));
      localStorage.setItem('user_data', JSON.stringify(userData));
      return;
    }

    const needsSync = userData.roles.some((r) => parseRoleId(r.id) === null);
    if (!needsSync && userData.roles.length >= ids.length) return;

    const payload = decodeAccessTokenPayload();
    const jwtUser = payload?.['User'] ?? payload?.['user'];
    const jwtRoles = Array.isArray((jwtUser as { roles?: unknown })?.roles)
      ? ((jwtUser as { roles: unknown[] }).roles as Array<{
          id?: unknown;
          name?: string;
          authority?: string;
        }>)
      : [];

    if (jwtRoles.length) {
      userData.roles = jwtRoles
        .map((r) => ({
          id: parseRoleId(r.id) ?? r.id,
          name: r.name ?? '',
          authority: r.authority ?? '',
        }))
        .filter((r) => parseRoleId(r.id) !== null);
    } else if (needsSync) {
      userData.roles = ids.map((id, index) => ({
        id,
        name: userData.roles?.[index]?.name ?? '',
        authority: userData.roles?.[index]?.authority ?? '',
      }));
    }

    localStorage.setItem('user_data', JSON.stringify(userData));
  } catch {
    /* ignore */
  }
}
